/**
 * <fd-wishlist>: the Fandom header heart.
 *
 * - Clicking it clicks ReStock's own `button.nm-wishlist-open-list-button`, which opens the app's list.
 * - The count is the number of items in the default list of localStorage.NM_customerData. It is refreshed on load,
 *   after clicks on ReStock UI, on focus / pageshow / visibilitychange and on the `storage` event, and written into
 *   every `[data-fd-wishlist-count]` (header badge and the mobile panel's "Wishlist (N)" button).
 * - While active, `body.fd-wishlist-on` hides ReStock's floating button (CSS in snippets/fd-wishlist-icon.liquid).
 *   If ReStock's button is not on the page ~6 s after load, the heart and the panel button hide and the class is
 *   removed, so the floating button comes back if the app appears later.
 * Never throws when localStorage is unavailable or holds garbage.
 */

const STORAGE_KEY = 'NM_customerData';
const APP_BUTTON = 'button.nm-wishlist-open-list-button';
const ACTIVE_CLASS = 'fd-wishlist-on';
const GRACE_MS = 6000;
const REFRESH_DELAYS = [300, 1200, 2500];

/** @type {Set<HTMLElement>} */
const instances = new Set();
/** @type {Set<number>} */
const timers = new Set();
/** @type {MutationObserver | null} */
let observer = null;
let graceTimer = 0;
let started = false;

const appButton = () => document.querySelector(APP_BUTTON);

function readCount() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw);
    const lists = Array.isArray(data && data.wishlists) ? data.wishlists : [];
    const list = lists.find((l) => l && l.is_default) || lists[0];
    return Array.isArray(list && list.items) ? list.items.length : 0;
  } catch (error) {
    return 0;
  }
}

/** @param {HTMLElement} el @param {number} n */
function labelFor(el, n) {
  const d = el.dataset;
  if (n <= 0) return d.labelZero || '';
  if (n === 1) return d.labelOne || d.labelZero || '';
  return (d.labelMany || d.labelZero || '').replace('__N__', String(n));
}

function renderCount() {
  const n = readCount();
  document.querySelectorAll('[data-fd-wishlist-count]').forEach((el) => {
    el.textContent = String(n);
    if (el.closest('fd-wishlist')) /** @type {HTMLElement} */ (el).hidden = n === 0;
  });
  instances.forEach((el) => {
    const label = labelFor(el, n);
    const btn = el.querySelector('[data-fd-wishlist-open]');
    if (btn && label) btn.setAttribute('aria-label', label);
  });
}

/** @param {boolean} on */
function setActive(on) {
  if (document.body) document.body.classList.toggle(ACTIVE_CLASS, on);
  instances.forEach((el) => {
    el.hidden = !on;
  });
  // The mobile panel's "Wishlist (N)" button (Task 7) hides with the heart. The hidden attribute (not an inline style)
  // lets the CSS in snippets/fd-wishlist-icon.liquid give "My account" the whole row.
  document.querySelectorAll('[data-fd-wishlist-count]').forEach((count) => {
    if (count.closest('fd-wishlist')) return;
    const row = count.closest('button, a');
    if (row instanceof HTMLElement) row.hidden = !on;
  });
}

function scheduleRefresh() {
  renderCount();
  REFRESH_DELAYS.forEach((ms) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      renderCount();
    }, ms);
    timers.add(id);
  });
}

let pendingOpen = false;

function openList() {
  const button = appButton();
  if (!(button instanceof HTMLElement)) {
    // ReStock is not there yet: keep the request and open the list when its button appears (within the grace period).
    pendingOpen = true;
    return;
  }
  pendingOpen = false;
  document.dispatchEvent(new CustomEvent('fd-wishlist:open'));
  button.click();
}

/** @param {Event} event */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const opener = target.closest('[data-fd-wishlist-open]');
  const panelRow = target.closest('button, a');
  const isPanelButton =
    panelRow && !panelRow.closest('fd-wishlist') && panelRow.querySelector('[data-fd-wishlist-count]');
  if (opener || isPanelButton) {
    event.preventDefault();
    openList();
    return;
  }
  if (target.closest('[class*="nm-wishlist"]')) scheduleRefresh();
}

/** @param {StorageEvent} event */
function onStorage(event) {
  if (event.key === null || event.key === STORAGE_KEY) renderCount();
}

function onVisibility() {
  if (document.visibilityState === 'visible') renderCount();
}

function watchForApp() {
  if (!observer) {
    observer = new MutationObserver(() => {
      if (!appButton()) return;
      window.clearTimeout(graceTimer);
      setActive(true);
      if (observer) observer.disconnect();
      observer = null;
      if (pendingOpen) openList();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
  const startGrace = () => {
    window.clearTimeout(graceTimer);
    graceTimer = window.setTimeout(() => {
      if (!appButton()) {
        pendingOpen = false;
        setActive(false);
      }
    }, GRACE_MS);
  };
  if (document.readyState === 'complete') startGrace();
  else window.addEventListener('load', startGrace, { once: true });
}

function start() {
  if (started) return;
  started = true;
  document.addEventListener('click', onClick);
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', renderCount);
  window.addEventListener('pageshow', renderCount);
  document.addEventListener('visibilitychange', onVisibility);
  setActive(true);
  if (!appButton()) watchForApp();
}

function stop() {
  if (!started) return;
  started = false;
  document.removeEventListener('click', onClick);
  window.removeEventListener('storage', onStorage);
  window.removeEventListener('focus', renderCount);
  window.removeEventListener('pageshow', renderCount);
  document.removeEventListener('visibilitychange', onVisibility);
  timers.forEach((id) => window.clearTimeout(id));
  timers.clear();
  window.clearTimeout(graceTimer);
  if (observer) observer.disconnect();
  observer = null;
  if (document.body) document.body.classList.remove(ACTIVE_CLASS);
}

class FdWishlist extends HTMLElement {
  connectedCallback() {
    instances.add(this);
    start();
    renderCount();
  }

  disconnectedCallback() {
    instances.delete(this);
    if (instances.size === 0) stop();
  }
}

if (!customElements.get('fd-wishlist')) customElements.define('fd-wishlist', FdWishlist);
